import { Link, Navigate, useParams } from "react-router-dom";
import { Reveal } from "@/components/primitives";
import { PROJECTS } from "@/data/projects";

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const p = PROJECTS.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/projects" replace />;

  const idx = PROJECTS.indexOf(p);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <main className="bg-paper pt-16" data-testid="project-detail-page">
      <div className="relative">
        <img
          src={p.img}
          alt={`${p.name} — ${p.type}`}
          className="h-[52vh] w-full object-cover lg:h-[64vh]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-10 lg:px-12 lg:pb-14">
          <div className="mx-auto max-w-[1440px]">
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-paper/75">{p.type}</p>
            <h1 className="mt-3 font-serif text-4xl text-paper sm:text-5xl lg:text-6xl" data-testid="project-detail-title">
              {p.name}
            </h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-6 py-16 lg:px-12 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-12">
          {p.desc && (
            <div className="lg:col-span-7">
              <Reveal>
                <h2 className="font-serif text-3xl text-ink">The project</h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/70" data-testid="project-detail-desc">
                  {p.desc}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/70">
                  Delivered under a single contract by the integrated ARA Turnkey team; design, engineering and
                  execution held together from briefing to sign-off, with stage-wise quality reports at every step.
                </p>
              </Reveal>
            </div>
          )}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border-t border-ink/15" data-testid="project-detail-meta">
                {[
                  ["Type of space", p.type],
                  ["Area", p.area],
                  ["Location", p.location],
                  ["Project type", p.scope],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-6 border-b border-ink/15 py-5">
                    <span className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">{k}</span>
                    <span className="text-right text-sm text-ink/85">{v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-10">
          {p.gallery.map((g, i) => (
            <Reveal key={i} delay={0.05}>
              <img
                src={g}
                alt={`${p.name} — view ${i + 1}`}
                loading="lazy"
                className={`w-full object-cover ${i === 1 ? "aspect-[16/7]" : "aspect-[16/9]"}`}
                data-testid={`project-detail-image-${i + 1}`}
              />
            </Reveal>
          ))}
        </div>

        <Link
          to={`/projects/${next.slug}`}
          data-testid="project-next-link"
          className="group mt-20 flex flex-col justify-between gap-6 border-t border-ink/15 pt-10 lg:flex-row lg:items-center"
        >
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">Next project</p>
            <p className="mt-3 font-serif text-3xl text-ink transition-colors duration-300 group-hover:text-maroon lg:text-4xl">
              {next.name}
            </p>
          </div>
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-maroon text-xl text-paper transition-transform duration-300 group-hover:translate-x-2">
            →
          </span>
        </Link>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-col items-start justify-between gap-8 bg-ink p-10 lg:flex-row lg:items-center lg:p-14">
            <h2 className="font-serif text-3xl text-paper lg:text-4xl">
              Planning something <em className="italic">similar</em>?
            </h2>
            <Link
              to="/contact"
              data-testid="project-detail-cta"
              className="inline-block rounded-full bg-maroon px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-transform duration-300 hover:scale-[1.04]"
            >
              Start a Project
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
